import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Logo from "../images/Logo.svg";
import { useLocation, Link } from "react-router-dom";
import Loader from "./Loader";
import { TailSpin } from "react-loader-spinner";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import "../styles/InvoiceDetail.css";
import { useAuth } from "../auth/auth";
import { PERMISSIONS } from "../utils/rbac";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
import html2pdf from "html2pdf.js";
import generatePdfIcon from "../images/generatePdfIcon.svg";
import generateCsvIcon from "../images/generateCsvIcon.svg";
const apiUrl = process.env.REACT_APP_API_URL;

const InvoiceDetail = () => {
  const location = useLocation();
  const invoiceId = location.state;
  const [invoice, setInvoice] = useState(null);
  const [rateString, setUniqueRatesString] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalAdditions, setTotalAdditions] = useState(0);
  const [totalDeductions, setTotalDeductions] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [pdfLoader, setPdfLoader] = useState(false);

  useAuth();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canGeneratePdf = isAllowed(PERMISSIONS.GENERATE_PDF);

  function formatInvoiceAdditions(additions) {
    const numericValue = Number(additions);

    if (isNaN(numericValue) || numericValue % 1 !== 0) {
      // Check if it's a decimal
      const formattedValue = numericValue.toFixed(2); // Ensure 2 digits after the decimal point
      return formattedValue;
    } else {
      // If it's an integer, add .00 to make it a decimal with 2 digits after the decimal point
      return additions + ".00";
    }
  }

  const calculateSummary = (formDataList) => {
    let totalAdditions = 0;
    let totalDeductions = 0;
    let totalAmount = 0;
    let subTotal = 0;

    formDataList.forEach((formData) => {
      totalAdditions += parseFloat(formData.Additions || 0);
      totalDeductions += parseFloat(formData.Deductions || 0);
      totalAmount += parseFloat(calculateAmount(formData));
      subTotal += parseFloat(calculateSubTotal(formData));
    });

    return { totalAdditions, totalDeductions, totalAmount, subTotal };
  };

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  function formatValue(value) {
    return value === "0.00" ? "-" : value;
  }

  const roundTwo = (num) =>
    Math.round((parseFloat(num || 0) + Number.EPSILON) * 100) / 100;

  const calculateSubTotal = (formData) => {
    let amount = 0;

    if (formData.paymentType === "salary") {
      amount = roundTwo(formData.salary || 0);
    } else if (
      formData.paymentType === "hourly" ||
      formData.paymentType === "overtime"
    ) {
      const hours = parseFloat(formData.hours || 0);
      const minutes = parseFloat(formData.minutes || 0);

      let hoursWorked = hours + minutes / 60;
      hoursWorked = roundTwo(hoursWorked);

      const rate = parseFloat(formData.hourlyRate || 0);
      if (formData.paymentType === "overtime") {
        amount = roundTwo(hoursWorked * 1.5 * rate);
      } else {
        amount = roundTwo(hoursWorked * rate);
      }
    }
    return amount;
  };

  const calculateAmount = (formData) => {
    let amount = 0;

    if (formData.paymentType === "salary") {
      amount = roundTwo(formData.salary || 0);
    } else if (
      formData.paymentType === "hourly" ||
      formData.paymentType === "overtime"
    ) {
      let hours = parseFloat(formData.hours || 0);
      let minutes = parseFloat(formData.minutes || 0);

      let hoursWorked = roundTwo(hours + minutes / 60);

      const rate = parseFloat(formData.hourlyRate || 0);

      if (formData.paymentType === "overtime") {
        amount = roundTwo(hoursWorked * 1.5 * rate);
      } else {
        amount = roundTwo(hoursWorked * rate);
      }
    }
    // Adjust amount for additions and deductions
    const additions = roundTwo(formData.Additions || 0);
    const deductions = roundTwo(formData.Deductions || 0);

    amount = roundTwo(amount + additions - deductions);
    return amount;
  };

  const handleDownloadOption = (option) => {
    setShowOptions(false);

    if (option === "pdf") {
      handleGeneratePDF(); // Call your PDF generation function
    } else if (option === "csv") {
      generateCSV(invoice); // Call your CSV generation function
    }
  };

  function generateCSV(invoice) {
    const csvContent = [];

    // Add headers to the CSV
    csvContent.push(
      "S.no,Employee Id,Invoice Id,Name,Email,Pay Rate($),Hours,Additions,Deductions,Amount,YTD Amount,Issue-Date,Pay-Period,From"
    );

    // Add data rows to the CSV
    const rowData = [
      1, // S.no
      invoice.userId,
      invoice.id,
      invoice.name,
      invoice.email,
      invoice.payRate,
      `${invoice.hours}.${invoice.minutes}hr`,
      invoice.Additions,
      invoice.Deductions,
      invoice.Amount,
      invoice.YTDFinal || invoice.YTDAmount,
      `"${invoice.formattedTimestamp}"`, //issue date,
      `"${invoice.payPeriod}"`,
      "Surplus staff driver",
    ];
    csvContent.push(rowData.join(","));

    // Join rows with newline character
    const csvString = csvContent.join("\n");

    // Create a Blob containing the CSV data
    const blob = new Blob([csvString], { type: "text/csv" });

    // Create a link to download the CSV file
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "payslip-detail.csv";

    // Trigger a click on the link to initiate the download
    link.click();
  }

  const containerRef = useRef();

  const handleGeneratePDF = () => {
    const container = containerRef.current;

    // Exclude the admin-header section from the content
    const contentToPrint = container.cloneNode(true);
    const adminHeader = contentToPrint.querySelector(".admin-header");
    const generateButton = contentToPrint.querySelector(".generate-pdf-btn");
    if (adminHeader) {
      adminHeader.remove();
    }

    if (generateButton) {
      generateButton.remove();
    }
    contentToPrint.style.backgroundColor = "#EDF6F0";
    html2pdf(contentToPrint, {
      filename: "payslip-detail.pdf",
      image: { type: "jpeg", quality: 1.0 },
      html2canvas: { 
        scale: 10,
        windowWidth: 1200
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      output: function (pdf) {
        // Manually initiate the download using the blob
        const blob = pdf.output("blob");
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "payslip-detail.pdf";
        link.click();
      },
    });
  };

  const handleGeneratePDFNew = async () => {
    guardAction(PERMISSIONS.GENERATE_PDF, "generate PDF", async () => {
      setPdfLoader(true);
      try {
        let response = await axios.get(
          `${apiUrl}/download-payslips?type=custom&ids=${invoiceId}`,
          {
            responseType: "blob",
          }
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "Payslips.pdf";
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Failed to download payslips:", error);
        alert("Download failed. Please try again.");
      } finally {
        setPdfLoader(false);
      }
    });
  };

  function getAddressProperties(formDataList) {
    let parsedList = JSON.parse(formDataList);
    let addressPropertiesSet = new Set(parsedList.map((item) => item.property));
    if (addressPropertiesSet.size === 1) {
      return [...addressPropertiesSet][0];
    } else {
      return [...addressPropertiesSet].join(",");
    }
  }

  useEffect(() => {
    const fetchInvoiceDetail = async () => {
      try {
        setIsLoading(true);
        const response = await axios.post(`${apiUrl}/getInvoiceDetail`, {
          id: invoiceId,
        });
        //console.log(response.data.data);
        setInvoice(response.data.data);
        // Parse formDataList and calculate summary
        const parsedFormDataList = JSON.parse(response.data.data.formDataList);
        const summary = calculateSummary(parsedFormDataList);

        const uniqueRates = [
          ...new Set(
            parsedFormDataList
              .map((item) => item.hourlyRate)
              .filter((rate) => rate !== undefined && rate !== null)
          ),
        ];

        // Convert to comma-separated string
        const formattedRatesString = uniqueRates
          .map((rate) => `$${formatInvoiceAdditions(rate)}`)
          .join(", ");

        if (formattedRatesString) {
          setUniqueRatesString(formattedRatesString);
        } else {
          setUniqueRatesString("$" + response.data.data.payRate.toString());
        }

        // Set summary values
        setTotalAdditions(summary.totalAdditions);
        setTotalDeductions(summary.totalDeductions);
        setTotalAmount(summary.totalAmount);
        setSubTotal(summary.subTotal);
      } catch (error) {
        //console.error("Error fetching invoice detail:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInvoiceDetail();
  }, [invoiceId]);

  // if (!invoice) {
  //   return <Loader />;
  // }

  return (
    <div className="feedback-parent-container">
      <PermissionDialog />
      <NavBar />
      <TopNavBar />
      <div className="invoice-detail-container">
        {pdfLoader && <Loader />}
        <div className="admin-header">
          <div className="left-section invoice-detail-left-section">
            <h1 className="admin-header-title hide-invoice-header-title">
              <Link
                to="/invoiceList"
                className="admin-card-title-link custom-link"
              >
                <span className="fade-text">Payslip List</span>
              </Link>
              {" > "}
              <span className="admin-card-title-span top-heading-invoice-Detail">
                Payslip Detail
              </span>
            </h1>
          </div>
          <div className="right-section">
            <div className="download-right-buttons employee-invoice-download-right-btns">
              <button
                className={`generate-pdf-btn employee-invoice-pdf-btn ${deniedActionClass(canGeneratePdf)}`}
                onClick={handleGeneratePDFNew}
              >
                <img
                  src={generatePdfIcon}
                  alt="Download"
                  className="downloadIcon"
                />
                Generate PDF
              </button>

              {/* <button
                className="generate-pdf-btn generate-csv-btn"
                onClick={() => generateCSV(invoice)}
              >
                <img
                  src={generateCsvIcon}
                  alt="Download"
                  className="downloadIcon"
                />
                Generate CSV
              </button> */}
            </div>
          </div>
        </div>
        {isLoading && (
          <div className="spinner-container">
            <TailSpin
              type="TailSpin"
              color="rgba(135, 106, 237, 1)"
              height={70}
              width={70}
              timeout={5000}
            />
          </div>
        )}
        {invoice && isLoading == false && (
          <div className="invoice-centre-div" ref={containerRef}>
            <div className="invoice-header">
              <div className="invoice-header-left">
                <p className="invoice-heading-decoration">Payslip</p>
              </div>
              <div className="invoice-header-image-right">
                <img src={Logo} alt="Logo" />
              </div>
            </div>
            <div className="invoice-info">
              <p className="margin-zero">{`#${invoice.id}`}</p>
              <p className="margin-zero">
                <span className="issue-date-label">Issue Date - </span>
                {invoice.formattedTimestamp}
              </p>
            </div>
            <div className="green-separator"></div>
            <div className="invoice-middle-header">
              <p className="invoice-bold-text">From</p>
              <p className="invoice-bold-text">To</p>
            </div>
            <div className="invoice-middle-header">
              <p className="invoice-detail-p-text">{`Surplus Pc Inc`}</p>
              <p className="invoice-detail-p-text">{`${invoice.name}`}</p>
            </div>

            <div className="invoice-middle-header">
              <p className="pay-period">{`Pay Period`}</p>
              <p className="invoice-detail-p-text">{`${invoice.email}`}</p>
            </div>
            <div className="invoice-middle-header">
              <p className="invoice-detail-p-text">{`${invoice.payPeriod}`}</p>
              <p className="invoice-detail-p-text">{`Employee Id - #${invoice.userId}`}</p>
            </div>
            <div className="invoice-middle-header">
              {invoice.payment_method ? (
                <p className="pay-period">Payment Method</p>
              ) : (
                <p></p>
              )}
              <p className="invoice-detail-p-text">Pay Rate - {rateString}</p>
            </div>
            {invoice.payment_method && (
              <>
                <div className="invoice-middle-header">
                  <p
                    className={`invoice-detail-p-text payment_method ${
                      invoice.payment_method
                        ? invoice.payment_method
                            .toLowerCase()
                            .replace(/\s+/g, "-")
                        : "no-method"
                    }`}
                  >
                    {invoice.payment_method || "---"}
                  </p>
                </div>
              </>
            )}
            <div className="table-container">
              <table className="invoice-table payslipgeneratedtable even-invoice-table invoice-detail-table-main row-white-complete">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Additions</th>
                    <th>Deductions</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {JSON.parse(invoice.formDataList).map((data, index) => (
                    <tr key={index}>
                      <td>{data.property}</td>
                      <td>{capitalizeFirstLetter(data.paymentType)}</td>
                      <td>
                        {data.paymentType === "salary"
                          ? `$${formatInvoiceAdditions(data.salary || 0.0)}`
                          : `${data.hours}:${data.minutes
                              .toString()
                              .padStart(2, "0")} hrs`}
                      </td>
                      <td>
                        <div>
                          {formatValue(
                            formatInvoiceAdditions(data.Additions)
                          ) !== "-" && "$"}
                          {formatValue(formatInvoiceAdditions(data.Additions))}
                        </div>
                        <div>
                          {data.additionsType !== ""
                            ? `(${data.additionsType})`
                            : ""}
                        </div>
                      </td>
                      <td>
                        <div>
                          {formatValue(
                            formatInvoiceAdditions(data.Deductions)
                          ) !== "-" && "$"}
                          {formatValue(formatInvoiceAdditions(data.Deductions))}
                        </div>
                        <div>
                          {data.deductionsType !== ""
                            ? `(${data.deductionsType})`
                            : ""}
                        </div>
                      </td>
                      <td>${formatInvoiceAdditions(calculateAmount(data))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="table-summry-container">
                <div className="table-right">
                  {/* <table className="amount-table invoice-table even-invoice-table row-white invoice-detail-table-main">
                    <thead>
                      <tr>
                        <th>Amount</th>
                        <th>YTD Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>${formatInvoiceAdditions(invoice.Amount)}</td>
                        <td>${formatInvoiceAdditions(invoice.YTDFinal || invoice.YTDAmount)}</td>
                      </tr>
                    </tbody>
                  </table> */}
                </div>
                <div className="summary-invoice-detail">
                  <div>
                    <span className="generate-invoice-user-detail-left-text">
                      Sub Total:
                    </span>
                    <span className="user-particular">
                      {" "}
                      ${formatInvoiceAdditions(subTotal)}
                    </span>
                  </div>
                  <div>
                    <span className="generate-invoice-user-detail-left-text">
                      Additions:
                    </span>
                    <span className="user-particular">
                      {" "}
                      ${formatInvoiceAdditions(totalAdditions)}
                    </span>
                  </div>
                  <div>
                    <span className="generate-invoice-user-detail-left-text">
                      Deductions:
                    </span>
                    <span className="user-particular">
                      {" "}
                      ${formatInvoiceAdditions(totalDeductions)}
                    </span>
                  </div>
                  <div>
                    <span className="generate-invoice-user-detail-left-text">
                      Total:
                    </span>
                    <span className="user-particular total-amount-text">
                      {" "}
                      ${formatInvoiceAdditions(totalAmount)}
                    </span>
                  </div>
                  <div>
                    <span className="generate-invoice-user-detail-left-text">
                      YTD Amount:
                    </span>
                    <span className="user-particular">
                      {" "}
                      ${formatInvoiceAdditions(invoice.YTDFinal || invoice.YTDAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetail;
